import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pandoyLogo from '../../../assets/pandoy.png';
import {
  AUTH_AUDIENCE_SHOWCASES,
  type AuthAudienceId,
  type AuthPlantationCrop,
  pandoyQuestionForPlantation,
  showcaseForAudience,
  storiesForShowcase,
} from '../../../content/authFeatureShowcase';
import {
  AUTH_FONT_SANS,
  AUTH_GOLD,
  AUTH_GREEN_900,
  AUTH_PAPER,
  AUTH_SAGE,
} from './authTheme';

interface AuthFeatureShowcaseProps {
  activeAudience?: AuthAudienceId;
  plantationCrop?: AuthPlantationCrop;
  onAudienceChange?: (id: AuthAudienceId) => void;
  onPlantationCropChange?: (crop: AuthPlantationCrop) => void;
  /** Cycle BLO, Plantations, LGU, and Carbon on this interval. */
  autoAdvanceMs?: number;
}

function FieldBoundaryLines() {
  return (
    <svg
      className="pointer-events-none absolute inset-0 h-full w-full opacity-[0.16]"
      viewBox="0 0 600 720"
      preserveAspectRatio="none"
      aria-hidden
    >
      <path d="M -20 90 L 140 40 L 320 100 L 300 240 L 120 260 Z" fill="none" stroke="#f8faf7" strokeWidth="1" />
      <path d="M 140 40 L 320 100 L 480 60 L 620 130 L 560 260 L 300 240" fill="none" stroke="#f8faf7" strokeWidth="1" />
      <path d="M 120 260 L 300 240 L 340 420 L 160 470 L 60 380 Z" fill="none" stroke="#f8faf7" strokeWidth="1" />
      <path d="M 300 240 L 560 260 L 610 430 L 400 480 L 340 420 Z" fill="none" stroke="#f8faf7" strokeWidth="1" />
      <path d="M 160 470 L 340 420 L 400 480 L 380 640 L 180 660 Z" fill="none" stroke="#f8faf7" strokeWidth="1" />
      <path d="M 400 480 L 610 430 L 650 620 L 420 660 L 380 640 Z" fill="none" stroke="#f8faf7" strokeWidth="1" />
    </svg>
  );
}

export default function AuthFeatureShowcase({
  activeAudience,
  plantationCrop = 'banana',
  onAudienceChange,
  onPlantationCropChange,
  autoAdvanceMs,
}: AuthFeatureShowcaseProps) {
  const navigate = useNavigate();
  const [audienceId, setAudienceId] = useState<AuthAudienceId>(activeAudience ?? 'blo');
  const [crop, setCrop] = useState<AuthPlantationCrop>(plantationCrop === 'coconut' ? 'coconut' : 'banana');
  const [fading, setFading] = useState(false);

  useEffect(() => {
    if (activeAudience && activeAudience !== audienceId) {
      setFading(true);
      const t = window.setTimeout(() => {
        setAudienceId(activeAudience);
        setFading(false);
      }, 180);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [activeAudience, audienceId]);

  useEffect(() => {
    if (audienceId === 'plantation') {
      setCrop(plantationCrop === 'coconut' ? 'coconut' : 'banana');
    }
  }, [plantationCrop, audienceId]);

  const selectAudience = useCallback(
    (id: AuthAudienceId) => {
      if (id === audienceId) return;
      setFading(true);
      window.setTimeout(() => {
        setAudienceId(id);
        if (id === 'plantation') setCrop('banana');
        setFading(false);
        onAudienceChange?.(id);
      }, 180);
    },
    [audienceId, onAudienceChange]
  );

  useEffect(() => {
    if (!autoAdvanceMs || autoAdvanceMs < 1000) return undefined;
    const timer = window.setInterval(() => {
      const ids = AUTH_AUDIENCE_SHOWCASES.map((item) => item.id);
      const index = ids.indexOf(audienceId);
      const next = ids[(index + 1) % ids.length];
      selectAudience(next);
    }, autoAdvanceMs);
    return () => window.clearInterval(timer);
  }, [autoAdvanceMs, audienceId, selectAudience]);

  const selectCrop = useCallback(
    (next: AuthPlantationCrop) => {
      setCrop(next);
      onPlantationCropChange?.(next);
    },
    [onPlantationCropChange]
  );

  const showcase = showcaseForAudience(audienceId);
  const stories = storiesForShowcase(showcase, crop);
  const pandoyQuestion =
    audienceId === 'plantation' ? pandoyQuestionForPlantation(crop) : showcase.pandoy.question;
  const fadeClass = fading ? 'opacity-0' : 'opacity-100';

  return (
    <div className="relative z-[2] flex min-h-0 flex-1 flex-col" style={{ fontFamily: AUTH_FONT_SANS }}>
      <div
        className="mt-7 flex w-fit gap-1.5 rounded-[11px] p-1"
        style={{ background: 'rgba(255,255,255,0.07)' }}
        role="tablist"
        aria-label="Account type"
      >
        {AUTH_AUDIENCE_SHOWCASES.map((aud) => {
          const active = aud.id === audienceId;
          return (
            <button
              key={aud.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => {
                if (aud.id === 'project_developer') {
                  navigate('/sflma');
                  return;
                }
                selectAudience(aud.id);
              }}
              className="rounded-lg px-[15px] py-2 text-[12.5px] font-medium tracking-tight transition-colors"
              style={{
                background: active ? AUTH_PAPER : 'transparent',
                color: active ? AUTH_GREEN_900 : AUTH_SAGE,
              }}
            >
              {aud.tabLabel}
            </button>
          );
        })}
      </div>

      <div className="mt-1.5 flex gap-1.5" aria-hidden>
        {AUTH_AUDIENCE_SHOWCASES.map((aud) => (
          <i
            key={aud.id}
            className="block h-[5px] w-[5px] rounded-full"
            style={{ background: aud.id === audienceId ? AUTH_GOLD : 'rgba(245,243,236,0.25)' }}
          />
        ))}
      </div>

      <div className={`transition-opacity duration-[220ms] ease-out ${fadeClass}`}>
        <p
          className="mt-3 flex min-h-4 items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em]"
          style={{ color: AUTH_GOLD }}
        >
          <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: AUTH_GOLD }} />
          {showcase.roleHint}
        </p>

        {audienceId === 'plantation' ? (
          <div className="mt-3 flex gap-2">
            {(['banana', 'coconut'] as const).map((c) => {
              const on = crop === c;
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => selectCrop(c)}
                  className="rounded-full border px-3 py-1 text-xs capitalize transition-colors"
                  style={{
                    color: on ? AUTH_GREEN_900 : AUTH_SAGE,
                    background: on ? AUTH_SAGE : 'transparent',
                    borderColor: on ? AUTH_SAGE : 'rgba(169,203,178,0.35)',
                  }}
                >
                  {c}
                </button>
              );
            })}
          </div>
        ) : null}

        <div className="mt-[18px]" style={{ textShadow: '0 1px 18px rgba(8,28,18,0.45)' }}>
          <h2
            className="m-0 max-w-[12em] text-4xl font-semibold leading-tight tracking-tight"
            style={{ color: AUTH_PAPER }}
          >
            {showcase.headline}
          </h2>
          <p className="mt-5 max-w-[30em] text-sm leading-7" style={{ color: AUTH_SAGE }}>
            {showcase.tagline}
          </p>
        </div>

        <ul className="mt-4 flex flex-col gap-2">
          {stories.map((story) => (
            <li
              key={story.title}
              className="flex gap-3 rounded-[10px] px-3.5 py-2.5 backdrop-blur-[2px]"
              style={{
                background: 'rgba(8,28,18,0.28)',
                border: '1px solid rgba(245,243,236,0.12)',
              }}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                style={{ background: 'rgba(227,163,61,0.14)' }}
                aria-hidden
              >
                <i className={`${story.icon} text-[17px]`} style={{ color: AUTH_GOLD }} />
              </span>
              <div className="min-w-0">
                <p className="m-0 text-sm font-semibold" style={{ color: AUTH_PAPER }}>
                  {story.title}
                </p>
                <p className="m-0 mt-0.5 text-[12.8px] leading-[1.45]" style={{ color: AUTH_SAGE }}>
                  {story.help}
                </p>
              </div>
            </li>
          ))}
        </ul>
        {audienceId === 'project_developer' ? (
          <button
            type="button"
            onClick={() => navigate('/sflma')}
            className="mt-4 inline-flex rounded-lg px-3.5 py-2 text-sm font-semibold"
            style={{ background: AUTH_PAPER, color: AUTH_GREEN_900 }}
          >
            Open the SFLMA explorer
          </button>
        ) : null}
      </div>

      <div
        className={`mt-auto border-t pt-[15px] transition-opacity duration-[220ms] ease-out ${fadeClass}`}
        style={{ borderColor: 'rgba(245,243,236,0.14)' }}
      >
        <div className="mb-1.5 flex items-baseline gap-1.5">
          <img
            src={pandoyLogo}
            alt=""
            className="h-6 w-6 shrink-0 translate-y-0.5 object-contain"
            aria-hidden
          />
          <span className="text-[13.5px] font-semibold" style={{ color: AUTH_PAPER }}>
            PandoyAi
          </span>
          <span
            className="text-[10px] font-semibold uppercase tracking-[0.2em]"
            style={{ color: AUTH_SAGE }}
          >
            Ask the map
          </span>
        </div>
        <p className="mb-2 max-w-[32em] text-[12.5px] leading-[1.5]" style={{ color: AUTH_SAGE }}>
          {showcase.pandoy.line}
        </p>
        <p
          className="inline-block rounded-full border px-3.5 py-1.5 text-[12.5px] italic"
          style={{
            color: AUTH_PAPER,
            background: 'rgba(245,243,236,0.07)',
            borderColor: 'rgba(245,243,236,0.16)',
          }}
        >
          “{pandoyQuestion}”
        </p>
      </div>
    </div>
  );
}

export { FieldBoundaryLines };
